import { useRef, useState } from 'react';
import { useGameState } from '../hooks/useGameState';
import { getStoneById } from '../data/stones';
import {
  applyTap,
  calculateTodayGrowthPoints,
  maxStageForRarity,
  pointsToNextStage,
  thresholdForStage,
} from '../domain/economy';
import { StoneIcon } from '../components/StoneIcon';
import { ProgressBar } from '../components/ProgressBar';
import type { Screen } from '../App';

interface StoneDetailScreenProps {
  stoneId: string;
  onBack: () => void;
  onNavigate: (next: Screen, stoneId?: string) => void;
}

interface FloatingNumber {
  id: number;
  value: string;
  x: number;
  y: number;
}

export function StoneDetailScreen({ stoneId, onBack, onNavigate }: StoneDetailScreenProps) {
  const { state, ownedStone, setState } = useGameState();
  const stone = getStoneById(stoneId);
  const owned = ownedStone(stoneId);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [floating, setFloating] = useState<FloatingNumber[]>([]);
  const tapButtonRef = useRef<HTMLButtonElement | null>(null);
  const floatIdRef = useRef(0);

  if (!stone) {
    return (
      <div className="screen stone-detail">
        <p>石が見つかりません。</p>
        <button className="btn" onClick={onBack}>
          戻る
        </button>
      </div>
    );
  }

  const max = maxStageForRarity(stone.rarity);
  const todayPts = state.daily.tapUsed
    ? 0
    : calculateTodayGrowthPoints(state.daily.consecutiveDays);

  const isOwned = !!owned;
  const isSelected = state.selectedStoneId === stoneId;

  const handleSelect = () => {
    if (!isOwned) return;
    setState((s) => ({ ...s, selectedStoneId: stoneId }));
  };

  const handleTap = () => {
    if (!isOwned) return;
    if (state.selectedStoneId !== stoneId) {
      setState((s) => ({ ...s, selectedStoneId: stoneId }));
    }
    let gained = 0;
    let stageUped = false;
    let newStageVal = 0;
    setState((s) => {
      const result = applyTap(s);
      gained = result.gainedPoints;
      stageUped = result.stageUp;
      newStageVal = result.newStage;
      if (result.gainedPoints === 0) {
        setFeedback('今日の育成は完了済みです');
      } else {
        setFeedback(
          result.stageUp
            ? `✦ 段階が ${result.newStage} に上がりました!`
            : `+${result.gainedPoints} pt を獲得`,
        );
      }
      return result.state;
    });

    if (gained > 0) {
      // フローティング数字を追加
      const rect = tapButtonRef.current?.getBoundingClientRect();
      const x = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
      const y = rect ? rect.top : window.innerHeight / 2;
      const id = ++floatIdRef.current;
      setFloating((prev) => [
        ...prev,
        {
          id,
          value: stageUped ? `✦${newStageVal}` : `+${gained}`,
          x,
          y,
        },
      ]);
      setTimeout(() => {
        setFloating((prev) => prev.filter((f) => f.id !== id));
      }, 1300);
    }

    setTimeout(() => setFeedback(null), 2200);
  };

  // 次の段階の閾値(現在の累計ptとの差分)
  const nextThreshold =
    owned && owned.currentStage < max ? thresholdForStage(stone.rarity, owned.currentStage + 1) : 0;
  const remain = owned ? pointsToNextStage(stone, owned) : null;

  return (
    <div className="screen stone-detail">
      <button className="btn btn--ghost" onClick={onBack} type="button">
        ← 戻る
      </button>

      <div className="stone-detail__hero">
        <StoneIcon stone={stone} stage={owned?.currentStage ?? 0} size={180} />
        <h1>{stone.name}</h1>
        <p className="stone-detail__rarity">— {rarityLabel(stone.rarity)} —</p>
        <p className="stone-detail__lore">{stone.lore}</p>
      </div>

      {isOwned && owned ? (
        <div className="stone-detail__grow">
          <h2 style={{ textAlign: 'center', margin: 0 }}>育成</h2>
          <p style={{ textAlign: 'center', margin: 0, fontSize: '1rem' }}>
            段階 <strong style={{ color: 'var(--gold-bright)' }}>{owned.currentStage}</strong> / {max}
          </p>
          <ProgressBar
            current={owned.growthPoints}
            max={nextThreshold || owned.growthPoints}
            label={remain != null ? `次の段階まで` : '最大段階に到達'}
            color={stone.geometryParams.glowColor}
          />
          <p className="stone-detail__today">
            本日 <strong>+{todayPts}</strong> pt · 連続 {state.daily.consecutiveDays}日目
          </p>
          <button
            ref={tapButtonRef}
            className="btn btn--primary btn--big"
            onClick={handleTap}
            disabled={state.daily.tapUsed}
            type="button"
          >
            {state.daily.tapUsed ? '今日の育成は完了' : 'タップして光を育てる'}
          </button>
          {feedback && <div className="toast">{feedback}</div>}
        </div>
      ) : (
        <div className="stone-detail__unowned">
          <p>未所持の石です。</p>
          <p>ガチャを引いて迎えましょう。</p>
          <button className="btn btn--primary" onClick={() => onNavigate('gacha')}>
            ガチャへ
          </button>
        </div>
      )}

      {isOwned && !isSelected && (
        <div className="stone-detail__select">
          <button className="btn" onClick={handleSelect} type="button">
            この石を育成対象にする
          </button>
        </div>
      )}

      {/* フローティング数字 */}
      {floating.map((f) => (
        <div
          key={f.id}
          className="tap-feedback"
          style={{ left: f.x, top: f.y }}
        >
          {f.value}
        </div>
      ))}
    </div>
  );
}

function rarityLabel(r: 'low' | 'mid' | 'high'): string {
  if (r === 'low') return '低ティア';
  if (r === 'mid') return '中ティア';
  return '★ レア';
}
